import _ from "lodash";
import * as Inquiry from "./Inquiry"
import * as Characteristic from "@/helper/application/characteristic/Provider";
import {byId, getParents, getSpecificityById} from "@/helper/application/characteristic/Provider";
import * as CharacteristicType from "@/helper/characteristic/Type";
import {arrayIsNotEmpty} from "@/helper/Array";
import * as JobProfileGroupCharacteristicType from "@/helper/job-profile/group/characteristic/Type";
import {getLabel} from "@/helper/characteristic/Requirement";
import {characteristicMultiple} from "@/helper/application/Populate";

const EMPTY_DATA = {
	placeOfWork: undefined,
	companyType: undefined,
	companySize: undefined,
	functions: undefined,
	functionDetail: undefined,
	functionAssistance: undefined,
	functionLegalField: undefined,
	functionLegalRole: undefined,
	functionTaxField: undefined,
	functionTaxRole: undefined,
	careerLevels: undefined,
	industrialSectors: undefined,
	organisationalArea: undefined,
	management: undefined,
	customerContact: undefined,
	workingTime: undefined,
	relocationPremise: undefined,
	hiringDate: undefined,
	employmentMode: undefined,
	salary: undefined,
	hourlyRate: undefined,
	homeOffice: undefined,
	overtime: undefined,
	travelWillingness: undefined,
	parking: undefined,
	publicTransport: undefined,
	privateVehicleCompanyUsage: undefined,
	companyCar: undefined,
	citizenship: undefined,
	functionsExperience: undefined,
	functionLegalFieldExperience: undefined,
	careerLevelsExperience: undefined,
	industrialSectorsExperience: undefined,
	organisationalAreaExperience: undefined,
	education: undefined,
	languages: undefined,
	skills: undefined,
	softwareMain: undefined,
	softwareSpecial: undefined,
	drivingLicense: undefined,
	pilotLicense: undefined,
	benefits: undefined,
	searchStatus: undefined,
	age: undefined,
	gender: undefined,
	incognitoMode: undefined,
}

const CONFIG = {
	[Inquiry.HIRING_DATE]: {
		property: 'hiringDate',
		fillPlain: true,
	},
	[Inquiry.WORKING_HOURS]: {
		property: 'workingHours',
		fillPlain: true,
	},
	[Inquiry.EMPLOYMENT_DURATION]: {
		property: 'employmentDuration',
		fillPlain: true,
	},
	[Inquiry.PLACE_OF_WORK]: {
		property: 'placeOfWork',
		populateCustom: ({group, meta}) =>
		{
			const extraGeo = _.filter(group.extraGeo, item => item.type === 'place-of-work');

			let data = {
				postalCodes: [],
			};

			_.forEach(extraGeo, extraGeoItem =>
			{
				const postalCodes = _.find(
					meta.geoPostalCodeCountryGroups,
					item => item.country.id === extraGeoItem.country.id
				).postalCodes;

				const postalCode = _.find(
					postalCodes,
					postalCode => postalCode.characteristic.id === extraGeoItem.characteristic.id
				);

				if (!postalCode) // should not happen
				{
					return true; // continue
				}

				// should be the same for all postal codes, so we can always set it here in the loop
				data.country = extraGeoItem.country.id;

				data.postalCodes.push(postalCode);
			});

			return data;
		}
	},
	[Inquiry.TRAVEL_WILLINGNESS]: {
		property: 'travelWillingness',
		populateCustom: ({group, meta}) =>
		{
			const data = JSON.parse(group.data);

			if (!data)
			{
				return undefined;
			}

			let populated = {
				necessary: data.necessary
			};

			if (!data.necessary)
			{
				return populated;
			}

			populated.geo = handleCharacteristicList(
				getGroupCharacteristicAllocationsByCharacteristicType(group, CharacteristicType.GEO),
				meta,
				meta.geo,
				meta.geoType,
			);

			populated.scope = handleCharacteristicList(
				getGroupCharacteristicAllocationsByCharacteristicType(group, CharacteristicType.TRAVEL_WILLINGNESS),
				meta,
				meta.travelWillingness,
				meta.travelWillingnessType
			);

			populated.externalSleepover = handleCharacteristicList(
				getGroupCharacteristicAllocationsByCharacteristicType(group, CharacteristicType.EXTERNAL_SLEEPOVER),
				meta,
				meta.externalSleepover,
				meta.externalSleepoverType,
			);

			return populated;
		}
	},
	[Inquiry.HOME_OFFICE]: {
		property: 'homeOffice',
		fillPlain: true,
	},
	[Inquiry.COMPANY_TYPE]: {
		property: 'companyType',
		populateCharacteristics: true,
		characteristicItems: 'companyType',
		characteristicType: 'companyTypeType',
	},
	[Inquiry.COMPANY_SIZE]: {
		property: 'companySize',
		populateCharacteristics: true,
		characteristicItems: 'companySize',
		characteristicType: 'companySizeType',
	},
	[Inquiry.EMPLOYMENT_MODE]: {
		property: 'employmentMode',
		populateCharacteristics: true,
		characteristicItems: 'employmentMode',
		characteristicType: 'employmentModeType',
	},
	[Inquiry.FUNCTION]: {
		property: 'functions',
		characteristicItems: 'function',
		characteristicType: 'functionType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_DETAIL]: {
		property: 'functionDetail',
		fillPlain: true,
	},
	[Inquiry.FUNCTION_ASSISTANCE]: {
		property: 'functionAssistance',
		characteristicItems: 'functionAssistance',
		characteristicType: 'functionAssistanceType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_LEGAL_FIELD]: {
		property: 'functionLegalField',
		characteristicItems: 'functionLegalField',
		characteristicType: 'functionLegalFieldType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_LEGAL_ROLE]: {
		property: 'functionLegalRole',
		characteristicItems: 'functionLegalRole',
		characteristicType: 'functionLegalRoleType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_TAX_FIELD]: {
		property: 'functionTaxField',
		characteristicItems: 'functionTaxField',
		characteristicType: 'functionTaxFieldType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_TAX_ROLE]: {
		property: 'functionTaxRole',
		characteristicItems: 'functionTaxRole',
		characteristicType: 'functionTaxRoleType',
		populateCharacteristics: true,
	},
	[Inquiry.FUNCTION_MARKETING_ROLE]: {
		property: 'functionMarketingRole',
		characteristicItems: 'functionMarketingRole',
		characteristicType: 'functionMarketingRoleType',
		populateCharacteristics: true,
	},
	[Inquiry.CAREER_LEVEL]: {
		property: 'careerLevels',
		characteristicItems: 'careerLevel',
		characteristicType: 'careerLevelType',
		populateCharacteristics: true,
	},
	[Inquiry.INDUSTRIAL_SECTOR]: {
		property: 'industrialSectors',
		characteristicItems: 'industrialSector',
		characteristicType: 'industrialSectorType',
		populateCharacteristics: true,
	},
	[Inquiry.ORGANISATIONAL_AREA]: {
		property: 'organisationalArea',
		characteristicItems: 'organisationalArea',
		characteristicType: 'organisationalAreaType',
		populateCharacteristics: true,
	},
	[Inquiry.WORKING_TIME]: {
		property: 'workingTime',
		characteristicItems: 'workingTime',
		characteristicType: 'workingTimeType',
		populateCharacteristics: true,
	},
	[Inquiry.CUSTOMER_CONTACT]: {
		property: 'customerContact',
		characteristicItems: 'businessModel',
		characteristicType: 'businessModelType',
		populateCharacteristics: true,
	},
	[Inquiry.RELOCATION_PREMISE]: {
		property: 'relocationPremise',
		characteristicItems: 'relocationPremise',
		characteristicType: 'relocationPremiseType',
		populateCharacteristics: true,
	},
	[Inquiry.MANAGEMENT]: {
		property: 'management',
		populateCharacteristics: true,
		characteristicItems: 'managementKind',
		characteristicType: 'managementKindType',
	},
	[Inquiry.SALARY]: {
		property: 'salary',
		fillPlain: true,
	},
	[Inquiry.HOURLY_RATE]: {
		property: 'hourlyRate',
		fillPlain: true,
	},
	[Inquiry.CITIZENSHIP]: {
		property: 'citizenship',
		populateCountries: true,
	},
	[Inquiry.DRIVING_LICENSE]: {
		property: 'drivingLicense',
		characteristicItems: 'drivingLicenseClass',
		characteristicType: 'drivingLicenseClassType',
		populateCharacteristics: true,
	},
	[Inquiry.PILOT_LICENSE]: {
		property: 'pilotLicense',
		characteristicItems: 'pilotLicense',
		characteristicType: 'pilotLicenseType',
		populateCharacteristics: true,
	},
	[Inquiry.OVERTIME]: {
		property: 'overtime',
		characteristicItems: 'overtime',
		characteristicType: 'overtimeType',
		populateCharacteristics: true,
	},
	[Inquiry.PARKING]: {
		property: 'parking',
		populateCharacteristics: true,
		characteristicItems: 'parking',
		characteristicType: 'parkingType',
	},
	[Inquiry.PUBLIC_TRANSPORT]: {
		property: 'publicTransport',
		populateCharacteristics: true,
		characteristicItems: 'publicTransport',
		characteristicType: 'publicTransportType',
	},
	[Inquiry.EXTERNAL_SLEEPOVER]: {
		property: 'externalSleepover',
		characteristicItems: 'externalSleepover',
		populateCharacteristic: true,
	},
	[Inquiry.PRIVATE_VEHICLE_COMPANY_USAGE]: {
		property: 'privateVehicleCompanyUsage',
		populateCharacteristics: true,
		characteristicItems: 'privateVehicleCompanyUsage',
		characteristicType: 'privateVehicleCompanyUsageType',
	},
	[Inquiry.COMPANY_CAR]: {
		property: 'companyCar',
		populateCharacteristics: true,
		characteristicItems: 'companyCar',
		characteristicType: 'companyCarType',
	},
	[Inquiry.BENEFITS]: {
		property: 'benefits',
		characteristicItems: 'benefits',
		characteristicType: 'benefitsType',
		populateCharacteristics: true,

	},
	[Inquiry.SEARCH_STATUS]: {
		property: 'searchStatus',
		characteristicItems: 'searchStatus',
		characteristicType: 'searchStatusType',
		populateCharacteristics: true,
	},
	[Inquiry.GENDER]: {
		property: 'gender',
		fillPlain: true,
	},
	[Inquiry.AGE]: {
		property: 'age',
		fillPlain: true,
	},
	[Inquiry.LANGUAGE]: {
		property: 'languages',
		characteristicItems: 'language',
		characteristicType: 'languageType',
		populateCharacteristics: true
	},
	[Inquiry.FUNCTION_EXPERIENCE]: {
		property: 'functionsExperience',
		characteristicItems: 'function',
		characteristicType: 'functionType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true
		}
	},
	[Inquiry.FUNCTION_LEGAL_FIELD_EXPERIENCE]: {
		property: 'functionLegalFieldExperience',
		characteristicItems: 'functionLegalField',
		characteristicType: 'functionLegalFieldType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			selfAssessmentIneligible: true
		}
	},
	[Inquiry.FUNCTION_LEGAL_ROLE_EXPERIENCE]: {
		property: 'functionLegalRoleExperience',
		characteristicItems: 'functionLegalRole',
		characteristicType: 'functionLegalRoleType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			experienceIneligible: true
		}
	},
	[Inquiry.FUNCTION_TAX_FIELD_EXPERIENCE]: {
		property: 'functionTaxFieldExperience',
		characteristicItems: 'functionTaxField',
		characteristicType: 'functionTaxFieldType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			selfAssessmentIneligible: true
		}
	},
	[Inquiry.FUNCTION_TAX_ROLE_EXPERIENCE]: {
		property: 'functionTaxRoleExperience',
		characteristicItems: 'functionTaxRole',
		characteristicType: 'functionTaxRoleType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			experienceIneligible: true
		}
	},
	[Inquiry.FUNCTION_MARKETING_ROLE_EXPERIENCE]: {
		property: 'functionMarketingRoleExperience',
		characteristicItems: 'functionMarketingRole',
		characteristicType: 'functionMarketingRoleType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			experienceIneligible: true
		}
	},
	[Inquiry.CAREER_LEVEL_EXPERIENCE]: {
		property: 'careerLevelsExperience',
		characteristicItems: 'careerLevel',
		characteristicType: 'careerLevelType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			selfAssessmentIneligible: true
		}
	},
	[Inquiry.INDUSTRIAL_SECTOR_EXPERIENCE]: {
		property: 'industrialSectorsExperience',
		characteristicItems: 'industrialSector',
		characteristicType: 'industrialSectorType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			selfAssessmentIneligible: true
		}
	},
	[Inquiry.ORGANISATIONAL_AREA_EXPERIENCE]: {
		property: 'organisationalAreaExperience',
		characteristicItems: 'organisationalArea',
		characteristicType: 'organisationalAreaType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			selfAssessmentIneligible: true
		}
	},
	[Inquiry.SKILLS]: {
		property: 'skills',
		characteristicItems: 'skill',
		characteristicType: 'skillType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true
		}
	},
	[Inquiry.SOFTWARE_MAIN]: {
		property: 'softwareMain',
		characteristicItems: 'skillSoftwareMain',
		characteristicType: 'skillSoftwareMainType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true
		}
	},
	[Inquiry.SOFTWARE_SPECIAL]: {
		property: 'softwareSpecial',
		characteristicItems: 'skillSoftwareSpecial',
		characteristicType: 'skillSoftwareSpecialType',
		populateCharacteristics: true,
		populateCharacteristicsConfig: {
			populateCheckType: true,
			addParents: false,
		}
	},
	[Inquiry.EDUCATION]: {
		property: 'education',
		populateCustom: ({group, meta}) =>
		{
			if (group.characteristics.length === 0)
			{
				return group.skipped ? undefined : null;
			}

			let schoolType = undefined;
			let professions = undefined;

			const schoolTypes = getGroupCharacteristicAllocations(group, JobProfileGroupCharacteristicType.SCHOOL_TYPE);

			if (arrayIsNotEmpty(schoolTypes))
			{
				schoolType = _.first(schoolTypes).characteristic;
			}

			const groupProfessions = getGroupCharacteristicAllocations(group, JobProfileGroupCharacteristicType.PROFESSION);

			if (groupProfessions && arrayIsNotEmpty(groupProfessions))
			{
				professions = handleCharacteristicList(
					groupProfessions,
					meta,
					meta.schoolProfession,
					meta.schoolProfessionType,
				);
			}

			return {
				schoolType,
				professions
			};
		}
	},
	[Inquiry.INCOGNITO_MODE]: {
		property: 'incognitoMode',
		fillPlain: true,
	},
};

export function prepare (jobProfile, meta)
{
	const data = _.cloneDeep(EMPTY_DATA);

	data.name = jobProfile.name;

	_.forEach(jobProfile.areas, area =>
	{
		_.forEach(area.groups, group =>
		{
			const config = CONFIG[group.inquiry.id];

			if (!config) // should not happen, but better than crashing
			{
				return true; // continue
			}

			const groupData = JSON.parse(group.data);

			if (config.fillPlain)
			{
				data[config.property] = groupData;

				return true; // continue
			}

			if (config.populateCustom)
			{
				data[config.property] = config.populateCustom({
					group,
					meta
				});

				return true; // continue
			}

			if (config.populateCharacteristic)
			{
				const characteristicAllocation = getGroupCharacteristicAllocation(group, null);

				data[config.property] = characteristicAllocation
					? Characteristic.byId(characteristicAllocation.characteristic.id, meta[config.characteristicItems])
					: null;

				return true; // continue
			}

			if (config.populateCharacteristics)
			{
				let preparedGroupData = [];

				// skipped without data, so undefined
				if (group.skipped && group.characteristics.length === 0)
				{
					data[config.property] = undefined;
					return;
				}

				if (!group.placeholder)
				{
					preparedGroupData = handleGroupCharacteristicsPopulation(
						preparedGroupData,
						group,
						meta,
						config
					);

					if (!data[config.property])
					{
						data[config.property] = [];
					}

					data[config.property] = _.concat(data[config.property], preparedGroupData);
				}
				else
				{
					_.forEach(group.subGroups, subGroup =>
					{
						preparedGroupData = handleGroupCharacteristicsPopulation(
							preparedGroupData,
							subGroup,
							meta,
							config
						);
					});

					data[config.property] = preparedGroupData;
				}

				return true; // continue
			}

			if (config.populateCharacteristicsFlat)
			{
				const metaItems = meta[config.characteristicItems];

				let groupData = [];

				if (group.characteristics && group.characteristics.length > 0)
				{
					_.forEach(group.characteristics, characteristicAllocation =>
					{
						const characteristic = byId(characteristicAllocation.characteristic.id, metaItems);

						groupData.push({
							item: characteristic,
						});
					});
				}

				data[config.property] = groupData;
			}

			if (config.populateCountries)
			{
				data[config.property] = [];

				_.forEach(group.countries, countryAllocation =>
				{
					const country = _.find(meta.country, c => c.id === countryAllocation.country.id);

					if (country)
					{
						data[config.property].push(country);
					}
				});

				return true; // continue
			}
		});
	});

	return data;
}

function transformSpecificities (specificities, characteristicType, characteristicItems)
{
	let transformed = [];

	_.forEach(specificities, specificityRaw =>
	{
		if (specificityRaw.specificityType)
		{
			const specificityTypeId = typeof specificityRaw.specificityType === 'string'
				? specificityRaw.specificityType
				: specificityRaw.specificityType.id;

			const specificityTypeAllocation = _.find(
				characteristicType.specificityTypes,
				allocation => allocation.specificityType.id === specificityTypeId
			);

			if (!specificityTypeAllocation)
			{
				return true; // continue
			}

			const specificityType = specificityTypeAllocation.specificityType;

			const specificityId = typeof specificityRaw.specificity === 'string'
				? specificityRaw.specificity
				: specificityRaw.specificity.id;

			const specificityAllocation = _.find(
				specificityType.allocations,
				alloc => alloc.specificity.id === specificityId
			);

			if (!specificityAllocation)
			{
				return true; // continue
			}

			transformed.push({
				specificityType,
				specificityTypeAllocationSort: specificityTypeAllocation.sort,
				specificity: specificityAllocation.specificity
			});
		}
		else
		{
			const specificityId = typeof specificityRaw.specificity === 'string'
				? specificityRaw.specificity
				: specificityRaw.specificity.id;

			const specificity = getSpecificityById(specificityId, characteristicItems);

			if (specificity)
			{
				transformed.push({
					specificity,
				});
			}
		}
	});

	return _.orderBy(transformed, ['specificityTypeAllocationSort'], ['asc']);
}

export function transformSupplements (supplements)
{
	if (!supplements || supplements.length === 0)
	{
		return [];
	}

	let grouped = [];

	_.forEach(supplements, supplement =>
	{
		const characteristicType = supplement.characteristicType;

		let group = _.find(grouped, group => group.characteristicType.id === characteristicType.id);

		if (!group)
		{
			group = {
				characteristicType,
				items: []
			};

			grouped.push(group);
		}

		group.items.push({
			item: supplement.characteristic
		});
	});

	return grouped;
}

function handleGroupCharacteristicsPopulation (preparedGroupData, group, meta, config)
{
	const characteristicItems = meta[config.characteristicItems];

	const characteristicAllocations = getGroupCharacteristicAllocations(group, null);

	if (characteristicAllocations.length === 0)
	{
		return preparedGroupData;
	}

	_.forEach(characteristicAllocations, characteristicAllocation =>
	{
		const characteristic = characteristicAllocation.characteristic
			? Characteristic.byId(characteristicAllocation.characteristic.id, characteristicItems)
			: null;

		let specificities = [];

		if (characteristicAllocation.specificities && arrayIsNotEmpty(characteristicAllocation.specificities))
		{
			specificities = transformSpecificities(
				characteristicAllocation.specificities,
				meta[config.characteristicType],
				characteristicItems
			);
		}

		let experience = undefined;
		let selfAssessment = undefined;
		let educations = undefined;
		let requirement = undefined;

		if (
			characteristicAllocation.experience !== undefined
			&& !config.populateCharacteristicsConfig?.experienceIneligible
		)
		{
			experience = characteristicAllocation.experience
				? byId(characteristicAllocation.experience.id, meta.workExperience)
				: null;
		}

		if (
			characteristicAllocation.selfAssessment
			&& !config.populateCharacteristicsConfig?.selfAssessmentIneligible
		)
		{
			selfAssessment = byId(characteristicAllocation.selfAssessment.id, meta.skillSelfAssessment);
		}

		if (characteristicAllocation.educations)
		{
			educations = _.map(characteristicAllocation.educations, allocation =>
			{
				return byId(allocation.characteristic.id, meta.schoolType);
			});
		}

		if (config.populateCharacteristicsConfig?.populateCheckType && group.checkType)
		{
			requirement = {
				id: group.checkType,
				label: getLabel(group.checkType)
			};
		}

		preparedGroupData.push({
			item: characteristic,
			specificities,
			supplements: transformSupplements(characteristicAllocation.supplements),
			experience,
			selfAssessment,
			educations,
			requirement,
			freeText: characteristicAllocation.freeText
		});

		// add parents as well for "whole tree"
		if (characteristic && config.populateCharacteristicsConfig?.addParents !== false)
		{
			const parents = getParents(characteristic.id, characteristicItems);

			if (parents)
			{
				_.forEach(parents, parent =>
				{
					if (!_.find(preparedGroupData, item => item.item.id === parent.id))
					{
						preparedGroupData.push({
							item: parent,
						});
					}
				});
			}
		}
	});

	return preparedGroupData;
}

function handleCharacteristicList (characteristicAllocations, meta, metaItems, metaType)
{
	let transformed = [];

	_.forEach(characteristicAllocations, allocation =>
	{
		const characteristic = byId(allocation.characteristic.id, metaItems);

		transformed.push({
			item: characteristic,
			specificities: transformSpecificities(allocation.specificities, metaType, metaItems),
			supplements: transformSupplements(allocation.supplements)
		});
	});

	transformed = characteristicMultiple(transformed, meta, metaItems, metaType, true);

	return transformed;
}

function getGroupCharacteristicAllocation (group, allocationType)
{
	const items = getGroupCharacteristicAllocations(group, allocationType);

	return items.length > 0
		? _.first(items)
		: null;
}

function getGroupCharacteristicAllocations (group, allocationType)
{
	return _.filter(
		group.characteristics || [],
		allocation => allocation.type === allocationType
	);
}

function getGroupCharacteristicAllocationsByCharacteristicType (group, characteristicType)
{
	return _.filter(
		group.characteristics,
		allocation => allocation.characteristic.type.id === characteristicType
	);
}

